/**
 * Aba "Recapagens": o ciclo no reformador. Envio e retorno são movimentações
 * (mudam a situação do pneu); aqui se acompanha o andamento e se importa,
 * como complemento, o que a Finatto registrou.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { TireRetreadRowDto, TireRetreadStatusDto } from "@bi/types";
import { api, ApiError } from "../../../lib/api";
import { fmtBRL } from "../../../lib/format";
import { EmptyState } from "../../../components/EmptyState";
import { IconRefresh } from "../../../components/icons";
import type { MoveTirePreset } from "../MoveTireModal";
import { fmtDate, fmtKm, OriginBadge, RETREAD_STATUS, TabBar, useTireCapabilities } from "../tire-ui";

type Filtro = "open" | "all" | "returned";

export function TireRetreadsTab({ onMove }: { onMove: (p: MoveTirePreset) => void }) {
  const { can } = useTireCapabilities();
  const qc = useQueryClient();
  const [filtro, setFiltro] = useState<Filtro>("open");
  const [msg, setMsg] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["tires", "retreads", filtro],
    queryFn: () => api.get<{ total: number; rows: TireRetreadRowDto[] }>(
      `/fleet/tire-retreads?limit=200${filtro === "open" ? "&open=1" : filtro === "returned" ? "&status=returned" : ""}`,
    ),
  });

  const andamento = useMutation({
    mutationFn: ({ id, status }: { id: string; status: TireRetreadStatusDto }) => api.patch(`/fleet/tire-retreads/${id}`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tires"] }),
    onError: (e) => setMsg(e instanceof ApiError ? e.message : "Não foi possível atualizar."),
  });
  const importar = useMutation({
    mutationFn: () => api.post<{
      shipments: { ok: boolean; created: number; error?: string }; returns: { ok: boolean; matched: number; created: number; error?: string };
    }>("/fleet/tire-retreads/import-finatto", {}),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["tires"] });
      setMsg(
        `Finatto: ${r.shipments.ok ? `${r.shipments.created} envio(s) novo(s)` : `envios indisponíveis (${r.shipments.error ?? "erro"})`} · ` +
        `${r.returns.ok ? `${r.returns.matched + r.returns.created} retorno(s)` : `retornos indisponíveis (${r.returns.error ?? "erro"})`}.`,
      );
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : "Importação indisponível agora."),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <TabBar<Filtro> tabs={[{ key: "open", label: "No reformador" }, { key: "returned", label: "Retornadas" }, { key: "all", label: "Todas" }]}
          active={filtro} onChange={setFiltro} />
        <div className="ml-auto flex gap-2">
          {can("tires.retread") && (
            <button className="btn-secondary" disabled={importar.isPending} onClick={() => { setMsg(null); importar.mutate(); }}>
              {importar.isPending ? "Importando…" : "Importar da Finatto"}
            </button>
          )}
          {can("tires.move") && <button className="btn-primary" onClick={() => onMove({ action: "to_retread" })}>Enviar para recapagem</button>}
        </div>
      </div>
      {msg && <p className="text-sm text-gray-600 bg-surface-muted rounded-lg px-4 py-2">{msg}</p>}

      <div className="card overflow-x-auto">
        {q.isLoading ? <p className="p-6 text-sm text-gray-500">Carregando…</p>
          : (q.data?.rows ?? []).length === 0 ? (
            <EmptyState icon={<IconRefresh className="w-5 h-5" />} title={filtro === "open" ? "Nenhum pneu no reformador" : "Nenhuma recapagem registrada"}
              description="O envio para recapagem é feito em “Movimentar pneu”. O histórico da Finatto pode ser importado como complemento." />
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-surface-muted text-xs text-gray-500 uppercase">
                <tr>
                  <th className="text-left px-4 py-2">Pneu</th>
                  <th className="text-left px-4 py-2">Nº</th>
                  <th className="text-left px-4 py-2">Reformador</th>
                  <th className="text-left px-4 py-2">Envio</th>
                  <th className="text-left px-4 py-2">Retorno</th>
                  <th className="text-left px-4 py-2 hidden md:table-cell">Banda</th>
                  <th className="text-right px-4 py-2">Custo</th>
                  <th className="text-left px-4 py-2">Situação</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {q.data!.rows.map((r) => (
                  <tr key={r.id}>
                    <td className="px-4 py-3 font-mono"><Link className="hover:underline" to={`/frota/pneus/${r.tireId}`}>{r.tireCode}</Link></td>
                    <td className="px-4 py-3 text-gray-600">R{r.sequence}</td>
                    <td className="px-4 py-3 text-gray-700">{r.supplierName ?? "—"} <OriginBadge origin={r.source} /></td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {fmtDate(r.sentAt)}{r.sentOdometerKm != null && <span className="block text-xs text-gray-400">{fmtKm(r.sentOdometerKm)}</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {r.returnedAt ? fmtDate(r.returnedAt) : r.daysOut != null ? <span className={r.daysOut > 30 ? "text-warning-700" : ""}>{r.daysOut} dia(s) fora</span> : "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{r.treadPattern ?? "—"}</td>
                    <td className="px-4 py-3 text-right">{r.cost != null ? fmtBRL(r.cost) : "—"}</td>
                    <td className="px-4 py-3"><span className={RETREAD_STATUS[r.status].badge}>{RETREAD_STATUS[r.status].label}</span></td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {r.source === "siag" && r.open && can("tires.retread") && r.status === "sent" && (
                        <button className="btn-ghost text-xs" onClick={() => andamento.mutate({ id: r.id, status: "in_process" })}>Em processo</button>
                      )}
                      {r.source === "siag" && r.open && can("tires.move") && (
                        <button className="btn-ghost text-xs" onClick={() => onMove({ tireId: r.tireId, action: "from_retread" })}>Registrar retorno</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>
    </div>
  );
}
